import { ColumnEditorOptions } from "primereact/column";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { useState } from "react";
import { QuestionOption } from "../../../types/checklist";

interface MultiChoiceEditorProps {
    options: ColumnEditorOptions;
    questionOptions: QuestionOption[];
}

// The value can arrive as an array or a JSON-encoded string.
const toIdArray = (value: unknown): string[] => {
    let parsed = value;
    if (typeof parsed === "string") {
        try { parsed = JSON.parse(parsed); } catch { return []; }
    }
    return Array.isArray(parsed) ? parsed : [];
};

export const MultiChoiceEditor = ({ options, questionOptions }: MultiChoiceEditorProps) => {
    // Seed once so the current selection shows on open and survives re-renders.
    const [value, setValue] = useState<string[]>(() => toIdArray(options.value));

    const handleChange = (e: MultiSelectChangeEvent) => {
        setValue(e.value);
        options.editorCallback?.(e.value);
    };

    const multiSelectOptions = questionOptions.map((opt) => ({
        label: opt.text,
        value: opt.id,
    }));

    return (
        <MultiSelect
            value={value}
            options={multiSelectOptions}
            onChange={handleChange}
            placeholder="Select options"
            className="w-full"
            display="chip"
            autoFocus
            // Render the panel inside the cell. Portaled to the body (default) its
            // option clicks read as "outside the cell" and complete the edit after
            // one pick; inline, you can select several and it saves once on blur.
            appendTo="self"
        />
    );
};

export const multiChoiceDisplayTemplate = (value: unknown, questionOptions?: QuestionOption[]): string => {
    if (!value) return "";

    // Parse value if it's a string
    let values = value;
    if (typeof values === "string") {
        try {
            values = JSON.parse(values);
        } catch {
            return String(value);
        }
    }

    if (!Array.isArray(values)) return String(value);
    if (!questionOptions) return values.join(", ");

    return values
        .map((v) => {
            const option = questionOptions.find((opt) => opt.id === v);
            return option?.text ?? v;
        })
        .join(", ");
};
